import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { NewInboxStore } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/store/newInboxStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IParamsAssignNumbersToInbox,
  type IParamsCreateInbox,
  type IResponseInbox,
} from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox'
import { AddSalesmsgNumbersStore } from 'pages/settings/store/newInboxSteps/addSalesmsgNumbersStore'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { ChooseNumberStore } from 'pages/settings/store/newInboxSteps/chooseNumberStore'
import { SelectAppAndNumbersStore } from 'pages/settings/ui/SelectAppAndNumbersCommon/store/SelectAppAndNumbersStore'
import { type IAddNumberType } from 'pages/settings/lib/addNumberType'

type INewInboxStepsCommonStoreConfig = {
  nextClickHandler?: () => void
  onPaymentNotRequired?: () => void
  getAddNumberType: () => IAddNumberType
  getSelectedNumbersCount: () => number
}

export type IGeneralStoreConfig = {
  nextClickHandler?: () => void
}

type ICommonCallbacks = {
  onSuccess: (inbox: IResponseInbox) => Promise<void> | void
  onError?: (error: unknown) => void
}

export class NewInboxStepsCommonStore {
  readonly newInboxStore: NewInboxStore
  readonly addSalesmsgNumbersStore: AddSalesmsgNumbersStore
  readonly paymentStore: PaymentCommonStore
  readonly chooseNumberStore: ChooseNumberStore
  readonly selectAppAndNumbersStore: SelectAppAndNumbersStore
  private _stepLoading = false

  constructor(readonly entities: IEntitiesStore, readonly config: INewInboxStepsCommonStoreConfig) {
    this.newInboxStore = new NewInboxStore(this.entities)
    this.addSalesmsgNumbersStore = new AddSalesmsgNumbersStore(
      {
        nextClickHandler: this.handleAddNumbersNextStep,
      },
      this.entities
    )
    this.paymentStore = new PaymentCommonStore(() => {}, this.entities)
    this.chooseNumberStore = new ChooseNumberStore(
      {
        nextClickHandler: this.config.nextClickHandler,
      },
      this.entities
    )
    this.selectAppAndNumbersStore = new SelectAppAndNumbersStore(this.entities)
    makeAutoObservable(this)
  }

  init = async () => {
    await this.entities.numbersStore.fetchUnassignedNumbers(true)
    this.addSalesmsgNumbersStore.init(this.unassignedNumbers)
    this.entities.complianceStore.loadNumberVendors()
    await this.entities.subscriptionStore.init()
  }

  get unassignedNumbers() {
    return this.entities.numbersStore.unassignedNumbersUser.filter(
      (number) => !(number.isLandline && number.status !== 'verifiedLandline')
    )
  }

  get unassignedNumbersCount() {
    return this.unassignedNumbers.length
  }

  get nextDisabled() {
    return this.newInboxStore.nextDisabled
  }

  get isTwilioNumberVendor() {
    return this.entities.organizationStore.isTwilioNumberVendor
  }

  get licensesCount() {
    return this.entities.subscriptionStore.licensesCount
  }

  get isGracePeriod() {
    return this.entities.organizationStore.isNumbersGracePeriod
  }

  get availableLicensesCount() {
    return this.paymentStore.licensesCount
  }

  get isManagerRole() {
    return this.entities.usersStore.user?.isManagerRole
  }

  get isBillingSettingsAccess() {
    return this.entities.userPermissionsStore.can_manage_billing_settings
  }

  get shouldHideAddNumbersFlow() {
    return this.isManagerRole || !this.isBillingSettingsAccess
  }

  get isBuyingNumber() {
    return this.config.getAddNumberType() === 'buy'
  }

  get stepLoading() {
    return this._stepLoading
  }

  get selectedNumbersCount() {
    return this.config.getSelectedNumbersCount()
  }

  get requiredLicensesToBuy() {
    const numbersCount = this.selectedNumbersCount
    const availableLicenses = this.availableLicensesCount

    if (availableLicenses === 0) return numbersCount
    if (availableLicenses < numbersCount) return numbersCount - availableLicenses
    return 0
  }

  get isPaymentStepEnabled() {
    return this.isTwilioNumberVendor && this.requiredLicensesToBuy > 0
  }

  get paymentNumbersList() {
    return this.config.getAddNumberType() === 'assign'
      ? this.addSalesmsgNumbersStore.numbersStore.multiSelectedValues
      : []
  }

  get shouldShowPaymentNumbersList() {
    return this.config.getAddNumberType() === 'assign' && this.paymentNumbersList.length > 1
  }

  handleAddNumbersNextStep = () => {
    if (this.isPaymentStepEnabled) {
      this.config.nextClickHandler?.()
    } else {
      this.config.onPaymentNotRequired?.()
    }
  }

  failureInboxCreated = (error: unknown) => {
    logger.error(error)
    if (error instanceof AxiosError) {
      const isPaymentError = error.errors.getError('stripe') === 'Your card was declined.'
      if (isPaymentError) {
        this.paymentStore.setPaymentError(true)

        return
      }
    }

    toastStore.add({
      type: 'error',
      title: 'Something went wrong. Please try again.',
    })
  }

  assignToNewInbox = async (
    params: Omit<IParamsAssignNumbersToInbox, 'numbers'>,
    callbacks: ICommonCallbacks
  ) => {
    try {
      runInAction(() => {
        this._stepLoading = true
      })

      const { data } = await InboxesApi.createInboxAndAssignNumber({
        ...params,
        numberIds: this.addSalesmsgNumbersStore.numbersStore.multiSelectedKeys,
        number: '',
      })
      this.addSalesmsgNumbersStore.reset()
      await callbacks.onSuccess(data)
    } catch (error) {
      this.failureInboxCreated(error)
      callbacks.onError?.(error)
      throw error
    } finally {
      runInAction(() => {
        this._stepLoading = false
      })
    }
  }

  buyNumberAndCreateInbox = async (
    params: Omit<IParamsCreateInbox, 'number' | 'vendor_id'>,
    callbacks: ICommonCallbacks
  ) => {
    try {
      runInAction(() => {
        this._stepLoading = true
      })

      const { selectedNumber, isUKSelected } = this.chooseNumberStore.chooseNumberTabsStore
      if (!selectedNumber) return

      const assignInboxData = {
        ...params,
        number: selectedNumber.number,
        vendor_id: '',
        is_mobile: isUKSelected || null,
      }

      const { data } = await InboxesApi.createInbox(assignInboxData)
      this.addSalesmsgNumbersStore.reset()
      await callbacks.onSuccess(data)
    } catch (error) {
      this.failureInboxCreated(error)
      callbacks.onError?.(error)
      throw error
    } finally {
      runInAction(() => {
        this._stepLoading = false
      })
    }
  }

  createInboxWithAppNumber = async (
    params: Omit<IParamsCreateInbox, 'number' | 'vendor_id'>,
    callbacks: ICommonCallbacks
  ) => {
    try {
      runInAction(() => {
        this._stepLoading = true
      })

      const { selectedNumber, selectedNumberVendorId } = this.selectAppAndNumbersStore
      if (!selectedNumber || !selectedNumberVendorId) return

      const assignInboxData: IParamsCreateInbox = {
        ...params,
        vendor_id: selectedNumber.id,
        number_vendor_id: selectedNumberVendorId,
        number: selectedNumber.formatted_number,
      }

      const { data } = await InboxesApi.createInbox(assignInboxData)
      this.addSalesmsgNumbersStore.reset()
      await callbacks.onSuccess(data)
    } catch (error) {
      this.failureInboxCreated(error)
      callbacks.onError?.(error)
      throw error
    } finally {
      runInAction(() => {
        this._stepLoading = false
      })
    }
  }

  resetStores = () => {
    this.addSalesmsgNumbersStore.numbersStore.handleMultiDeselect()
    this.chooseNumberStore.chooseNumberTabsStore.resetInboxNumberSelection()
    this.chooseNumberStore.resetUseCase()
    this.selectAppAndNumbersStore.reset()
  }
}
